import React, { useMemo, useState, useEffect } from "react";
import EasyScriptBuilder from "./ScriptFlow/EasyScriptBuilder.js";
import VariablesSidebar from "./VariablesSidebar.js";
import { normalizeSections, parseVariables } from "./ScriptFlow/scriptFlowUtils.js";
import ApiService from "../../services/Api.service.js";
import "./ScriptConfigTab.css";

export default function ScriptConfigTab({ selectedAgent, handleInputChange, formErrors = {} }) {
  const sections = useMemo(() => normalizeSections(selectedAgent), [selectedAgent]);

  const [isVariablesOpen, setIsVariablesOpen] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState("all");
  const [activeSectionTitle, setActiveSectionTitle] = useState("All Sections");
  const [preProcessingOptions, setPreProcessingOptions] = useState([]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const res = await ApiService.getPreProcessingOptions();
        if (!isMounted) return;
        const data = res?.data?.data || res?.data || [];
        setPreProcessingOptions(
          Array.isArray(data)
            ? data.map((o) =>
                typeof o === "string" ? { label: o, value: o } : { label: o.label || o.value || "", value: o.value || o.label || "" }
              )
            : []
        );
      } catch (err) {
        console.warn("Failed to load preprocessing options", err);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  // Compute variables map per section
  const sectionVarsMap = useMemo(() => {
    const map = {};
    sections.forEach((sec) => {
      map[sec.id] = parseVariables(sec.prompt || "");
    });
    return map;
  }, [sections]);

  const combinedVars = useMemo(() => {
    const set = new Set();
    Object.values(sectionVarsMap).forEach((list) => {
      list.forEach((v) => set.add(v));
    });
    return Array.from(set);
  }, [sectionVarsMap]);

  const dynamicSectionOptions = useMemo(() => {
    return [
      { value: "all", label: "All Sections" },
      ...sections.map((s) => ({ value: s.id, label: s.title || s.id }))
    ];
  }, [sections]);

  const getVariableValue = (varName, type = "value") => {
    if (!selectedAgent?.variables) return "";
    const vars = selectedAgent.variables;
    if (type === "option" || type === "preprocessing") {
      return vars.preprocessing_fields?.[varName] || "";
    }
    return (
      vars.additional_fields?.[varName] ??
      vars.preprocessing_fields?.[varName] ??
      ""
    );
  };

  const handleVariableChange = (varName, typeOrVal, newVal) => {
    let fieldType = "value";
    let val = typeOrVal;

    if (newVal !== undefined) {
      fieldType = typeOrVal;
      val = newVal;
    }

    const currentVars = selectedAgent?.variables || { preprocessing_fields: {}, additional_fields: {} };
    const nextVars = {
      preprocessing_fields: { ...(currentVars.preprocessing_fields || {}) },
      additional_fields: { ...(currentVars.additional_fields || {}) }
    };

    if (fieldType === "option" || fieldType === "preprocessing") {
      nextVars.preprocessing_fields[varName] = val;
    } else {
      nextVars.additional_fields[varName] = val;
    }

    handleInputChange("variables", nextVars);
  };

  const handleOpenVariablesForStep = (sectionId, sectionTitle) => {
    setActiveSectionId(sectionId || "all");
    setActiveSectionTitle(sectionTitle || "This Step");
    setIsVariablesOpen(true);
  };

  if (!selectedAgent) {
    return <div className="script-flow-empty">Select an agent to configure its conversation script.</div>;
  }

  return (
    <div className="script-flow-editor">
      {/* Main Guided Easy Builder */}
      <EasyScriptBuilder
        sections={sections}
        handleInputChange={handleInputChange}
        selectedAgent={selectedAgent}
        onOpenVariablesForStep={handleOpenVariablesForStep}
        formErrors={formErrors}
      />

      {/* Variables Sidebar Filtered by Step */}
      <VariablesSidebar
        isOpen={isVariablesOpen}
        onClose={() => setIsVariablesOpen(false)}
        activeSectionId={activeSectionId}
        activeSectionTitle={activeSectionTitle}
        sectionVarsMap={sectionVarsMap}
        combinedVars={combinedVars}
        getVariableValue={getVariableValue}
        handleVariableChange={handleVariableChange}
        preProcessingOptions={preProcessingOptions}
        dynamicSectionOptions={dynamicSectionOptions}
        onSectionFilterChange={(secId, secTitle) => {
          setActiveSectionId(secId);
          if (secTitle) setActiveSectionTitle(secTitle);
        }}
      />
    </div>
  );
}
